import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { IconArrowLeft, IconShield, IconSettings, IconPalette, IconWebhook, IconDashboard, IconTestPipe, IconRoute, IconCalendarEvent, IconMountain, IconMail, IconBike, IconMovie } from '@tabler/icons-react';
import { useAdminView } from '../contexts/AdminViewContext';
import './AdminMenuPage.css';

const AdminMenuPage = ({ user }) => {
  const navigate = useNavigate();
  const { isAdmin } = useAdminView();

  const handleBack = () => {
    navigate(-1);
  };

  if (!user || !isAdmin) {
    return (
      <div className="admin-menu-page">
        <div className="admin-not-authorized">
          <IconShield size={64} />
          <h2>Admin Access Required</h2>
          <p>You need administrator privileges to access this area.</p>
          <button onClick={() => user?.uid ? navigate(`/profile/${user.uid}`) : navigate('/')}>Back to Profile</button>
        </div>
      </div>
    );
  }

  const adminLinks = [
    {
      title: 'Admin Settings',
      description: 'Manage route line width, colors, monthly limits, and watermark detection',
      icon: IconSettings,
      path: '/admin-settings',
      color: '#3b82f6'
    },
    {
      title: 'Newsletter Sending',
      description: 'Preview recipients, send HTML newsletters, and monitor batch delivery progress',
      icon: IconMail,
      path: '/admin/newsletter',
      color: '#128251'
    },
    {
      title: 'Manage Prompts',
      description: 'Create, edit, and manage AI prompt templates for map generation',
      icon: IconPalette,
      path: '/prompt-management',
      color: '#8b5cf6'
    },
    {
      title: 'Events',
      description: 'Create events that unlock exclusive, time-boxed styles and grant diamonds',
      icon: IconCalendarEvent,
      path: '/admin/events',
      color: '#c98b53'
    },
    {
      title: 'Strava Webhooks',
      description: 'Configure and manage Strava webhook subscriptions',
      icon: IconWebhook,
      path: '/strava-webhook-admin',
      color: '#FC5200'
    },
    {
      title: 'Admin Dashboard',
      description: 'Monitor all map generations, manage users, and view analytics',
      icon: IconDashboard,
      path: '/admin/dashboard',
      color: '#10b981'
    },
    {
      title: 'Background Videos',
      description: 'Highlight videos made in the background for real activities: timings, costs and versions (not shown to users)',
      icon: IconMovie,
      path: '/admin/videos',
      color: '#ef4444'
    },
    {
      title: 'Test Comparisons',
      description: 'View and compare parallel test generations for prompt optimization',
      icon: IconTestPipe,
      path: '/admin/test-comparisons',
      color: '#6366f1'
    },
    {
      title: 'GPX Viewer',
      description: 'View and analyze GPX route files on an interactive map',
      icon: IconRoute,
      path: '/tools/gpx-viewer',
      color: '#14b8a6'
    },
    {
      title: 'Zwift Terrain Explorer',
      description: 'Explore Watopia 3D terrain, water, slope, and altitude-sample heatmaps',
      icon: IconMountain,
      path: '/admin/zwift-terrain',
      color: '#f97316'
    },
    {
      title: '3D Route Builder',
      description: 'Import a GPX or Strava route and build its world from open map data',
      icon: IconBike,
      path: '/admin/route3d',
      color: '#0e7490'
    },
    {
      title: 'Part-Assembly Experiments',
      description: 'Compare facade model versions, track live RunPod progress/ETA, and test multi-area Japan routes',
      icon: IconTestPipe,
      path: '/admin/experiments',
      color: '#a855f7'
    }
  ];

  return (
    <div className="admin-menu-page">
      <div className="admin-menu-container">
        <div className="admin-menu-header">
          <button className="back-button" onClick={handleBack}>
            <IconArrowLeft size={20} />
            Back
          </button>
          <div className="header-content">
            <div className="shield-icon">
              <IconShield size={40} />
            </div>
            <div>
              <h1>Admin Panel</h1>
              <p className="admin-menu-subtitle">Access administrative tools and settings</p>
            </div>
          </div>
        </div>
        <div className="admin-divider"></div>

        <div className="admin-links-grid">
          {adminLinks.map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.path}
                to={link.path}
                className="admin-link-card"
              >
                <div className="admin-link-icon" style={{ backgroundColor: `${link.color}20`, color: link.color }}>
                  <Icon size={28} />
                </div>
                <div className="admin-link-content">
                  <h3>{link.title}</h3>
                  <p>{link.description}</p>
                  {link.note && (
                    <span className="admin-link-note">{link.note}</span>
                  )}
                </div>
                <div className="admin-link-arrow" style={{ color: link.color }}>
                  →
                </div>
              </Link>
            );
          })}
        </div>

        <div className="admin-warning">
          <IconShield size={20} />
          <div>
            <strong>Admin Notice:</strong> These tools provide access to sensitive system settings.
            Exercise caution when making changes.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMenuPage;
